import type { ComponentType } from 'react'
import type { AdminRouterContext } from '@/lib/router-context'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type { Organization } from '@/lib/services/organizations/organizations.types'
import type { FsnNetworkIntegrationResponse } from '@/lib/services/fsn-network/fsn-network.types'
import type { CondolencesIntegration } from '@/lib/services/condolences/condolences.types'
import type { GoogleAnalyticsIntegration } from '@/lib/services/google-analytics/google-analytics.types'
import type { GoogleTagManagerIntegration } from '@/lib/services/google-tag-manager/google-tag-manager.types'
import type { MetaPixelIntegration } from '@/lib/services/meta-pixel/meta-pixel.types'
import type { PathResult } from '@/hooks/usePathHelper'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import CondolencesIcon from '@/assets/CondolencesIcon'
import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { BetaFeatureKey, hasBetaFeature } from '@/lib/beta-features'
import { useAbility, type AppAbility } from '@/lib/casl'
import {
  condolencesQueries,
  useCondolencesIntegration,
} from '@/lib/services/condolences/condolences.query'
import {
  fsnNetworkQueries,
  useFsnNetworkIntegration,
} from '@/lib/services/fsn-network/fsn-network.query'
import {
  googleAnalyticsQueries,
  useGoogleAnalyticsIntegration,
} from '@/lib/services/google-analytics/google-analytics.query'
import {
  googleTagManagerQueries,
  useGoogleTagManagerIntegration,
} from '@/lib/services/google-tag-manager/google-tag-manager.query'
import {
  metaPixelQueries,
  useMetaPixelIntegration,
} from '@/lib/services/meta-pixel/meta-pixel.query'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { ArrowRight, BarChart3, Laptop, Network, Tags, Target } from 'lucide-react'

type SettingsAbility = Pick<AppAbility, 'can'> | null | undefined
type SettingsOrganization = Pick<Organization, 'beta_feature_keys'> | null | undefined

// Disabled or loading queries use the existing empty-object defaults below.
type FsnIntegrationSummary = Partial<
  Pick<FsnNetworkIntegrationResponse, 'connected' | 'status_badge'>
>
type CondolencesIntegrationSummary = Partial<
  Pick<CondolencesIntegration, 'connected' | 'status_badge'>
>
type IntegrationSettingsUseCase = {
  id: string
  title: string
  description: string
  connected?: boolean
  statusBadge?:
    | FsnIntegrationSummary['status_badge']
    | CondolencesIntegrationSummary['status_badge']
    | null
  icon: ComponentType<{ className?: string }>
  link: PathResult
  show?: boolean
}

type IntegrationSettingsInputs = {
  ability: SettingsAbility
  condolencesIntegration?: CondolencesIntegrationSummary
  fsnNetworkIntegration?: FsnIntegrationSummary
  googleAnalyticsIntegration?: Partial<Pick<GoogleAnalyticsIntegration, 'connected'>>
  googleTagManagerIntegration?: Partial<Pick<GoogleTagManagerIntegration, 'connected'>>
  metaPixelIntegration?: Partial<Pick<MetaPixelIntegration, 'connected'>>
  organization?: SettingsOrganization
  orgId: OrganizationIdInput
  path: Pick<
    ReturnType<typeof usePathHelper>,
    | 'fsnNetwork'
    | 'condolences'
    | 'googleAnalytics'
    | 'googleTagManager'
    | 'metaPixel'
    | 'snapdragon'
  >
}

function canAccessBusinessSettings(ability: SettingsAbility) {
  return ability?.can('list', 'business-settings')
}

function canAccessFsnNetworkIntegration(ability: SettingsAbility) {
  return canAccessBusinessSettings(ability) && ability?.can('edit', 'fsn-network')
}

function canAccessCondolencesIntegration(ability: SettingsAbility) {
  return canAccessBusinessSettings(ability) && ability?.can('list', 'condolences')
}

function canAccessGoogleAnalyticsIntegration(ability: SettingsAbility) {
  return (
    canAccessBusinessSettings(ability) &&
    ability?.can('list', 'google-analytics') &&
    ability?.can('view', 'stores')
  )
}

function canAccessGoogleTagManagerIntegration(ability: SettingsAbility) {
  return ability?.can('list', 'google-tag-manager')
}

function canAccessMetaPixelIntegration(ability: SettingsAbility) {
  return ability?.can('list', 'meta-pixel')
}

function canAccessSnapdragonIntegration(
  ability: SettingsAbility,
  organization?: SettingsOrganization
) {
  return (
    ability?.can('list', 'snapdragon-settings') &&
    hasBetaFeature(organization, BetaFeatureKey.Snapdragon)
  )
}

export function hasIntegrationSettingsAccess(
  ability: SettingsAbility,
  organization?: SettingsOrganization
): boolean {
  return Boolean(
    canAccessFsnNetworkIntegration(ability) ||
    canAccessCondolencesIntegration(ability) ||
    canAccessGoogleAnalyticsIntegration(ability) ||
    canAccessGoogleTagManagerIntegration(ability) ||
    canAccessMetaPixelIntegration(ability) ||
    canAccessSnapdragonIntegration(ability, organization)
  )
}

export function buildIntegrationSettingsUseCases({
  ability,
  condolencesIntegration,
  fsnNetworkIntegration,
  googleAnalyticsIntegration,
  googleTagManagerIntegration,
  metaPixelIntegration,
  organization,
  orgId,
  path,
}: IntegrationSettingsInputs): IntegrationSettingsUseCase[] {
  return [
    {
      id: 'fsn-network',
      title: 'Flower Shop Network',
      description: 'Connect FSN for F2F order handling and out-of-area delivery workflows.',
      connected: Boolean(fsnNetworkIntegration?.connected),
      statusBadge: fsnNetworkIntegration?.status_badge ?? null,
      icon: FsnNetworkIcon,
      link: path.fsnNetwork.index().using({ orgId }),
      show: canAccessFsnNetworkIntegration(ability),
    },
    {
      id: 'condolences',
      title: 'Condolences',
      description: 'Import memorial orders and sync delivery or cancellation updates.',
      connected: Boolean(condolencesIntegration?.connected),
      statusBadge: condolencesIntegration?.status_badge ?? null,
      icon: CondolencesIcon,
      link: path.condolences.index().using({ orgId }),
      show: canAccessCondolencesIntegration(ability),
    },
    {
      id: 'google-analytics',
      title: 'Google Analytics',
      description: 'Send storefront visits and sales activity to your Google Analytics account.',
      connected: Boolean(googleAnalyticsIntegration?.connected),
      icon: BarChart3,
      link: path.googleAnalytics.index().using({ orgId }),
      show: canAccessGoogleAnalyticsIntegration(ability),
    },
    {
      id: 'google-tag-manager',
      title: 'Google Tag Manager',
      description: 'Run the tags from your Google Tag Manager container on your live website.',
      connected: Boolean(googleTagManagerIntegration?.connected),
      icon: Tags,
      link: path.googleTagManager.index().using({ orgId }),
      show: canAccessGoogleTagManagerIntegration(ability),
    },
    {
      id: 'meta-pixel',
      title: 'Meta Pixel',
      description: 'Send live storefront visits to your Meta advertising account.',
      connected: Boolean(metaPixelIntegration?.connected),
      icon: Target,
      link: path.metaPixel.index().using({ orgId }),
      show: canAccessMetaPixelIntegration(ability),
    },
    {
      id: 'snapdragon',
      title: 'Snapdragon',
      description: 'Connect this computer to your shop for Snapdragon services.',
      icon: Laptop,
      link: path.snapdragon.index().using({ orgId }),
      show: canAccessSnapdragonIntegration(ability, organization),
    },
  ].filter((integration) => integration.show)
}

function IntegrationSettingsCard({ integration }: { integration: IntegrationSettingsUseCase }) {
  const Icon = integration.icon
  const statusBadge = integration.statusBadge

  return (
    <Link
      {...integration.link.link}
      aria-label={`${integration.title}: ${integration.description}`}
      className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow] hover:border-primary/35 hover:shadow-sm"
    >
      <span className="mt-0.5 inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-interaction">
        <Icon className="size-5" />
      </span>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-1.5">
          <Typography variant="cardTitle">{integration.title}</Typography>
          {statusBadge?.label ? (
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
          ) : integration.connected ? (
            <Badge variant="success">Connected</Badge>
          ) : null}
        </div>
        <Typography variant="supporting" className="block">
          {integration.description}
        </Typography>
      </div>

      <ArrowRight className="size-5 shrink-0 self-center text-muted-foreground transition group-hover:translate-x-1 group-hover:text-interaction" />
    </Link>
  )
}

export async function integrationSettingsLoader({
  context,
  params,
}: {
  context: {
    ability: Pick<AdminRouterContext['ability'], 'can'>
    queryClient: AdminRouterContext['queryClient']
  }
  params: { orgId: string | number }
}): Promise<void> {
  if (
    !canAccessFsnNetworkIntegration(context.ability) &&
    !canAccessCondolencesIntegration(context.ability) &&
    !canAccessGoogleAnalyticsIntegration(context.ability) &&
    !context.ability.can('list', 'snapdragon-settings') &&
    !canAccessGoogleTagManagerIntegration(context.ability) &&
    !canAccessMetaPixelIntegration(context.ability)
  ) {
    throw notFound()
  }

  const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

  if (isGlobalOrg) {
    if (!hasIntegrationSettingsAccess(context.ability)) throw notFound()

    return
  }

  const organization = await context.queryClient.ensureQueryData(
    organizationQueries.show(params.orgId)
  )

  if (!hasIntegrationSettingsAccess(context.ability, organization)) throw notFound()

  const providerLoads = []

  if (canAccessFsnNetworkIntegration(context.ability)) {
    providerLoads.push(
      context.queryClient.ensureQueryData(
        fsnNetworkQueries.integration({ organizationId: params.orgId })
      )
    )
  }

  if (canAccessCondolencesIntegration(context.ability)) {
    providerLoads.push(
      context.queryClient.ensureQueryData(
        condolencesQueries.integration({ organizationId: params.orgId })
      )
    )
  }

  if (canAccessGoogleAnalyticsIntegration(context.ability)) {
    providerLoads.push(
      context.queryClient.ensureQueryData(
        googleAnalyticsQueries.integration({ organizationId: params.orgId })
      )
    )
  }

  if (canAccessGoogleTagManagerIntegration(context.ability)) {
    providerLoads.push(
      context.queryClient.ensureQueryData(
        googleTagManagerQueries.integration({ organizationId: params.orgId })
      )
    )
  }

  if (canAccessMetaPixelIntegration(context.ability)) {
    providerLoads.push(
      context.queryClient.ensureQueryData(
        metaPixelQueries.integration({ organizationId: params.orgId })
      )
    )
  }

  await Promise.all(providerLoads)
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/integrations/')({
  component: IntegrationSettingsPage,
  loader: integrationSettingsLoader,
})

function OrganizationIntegrationSettings() {
  const orgId = useRouteOrganizationIdParam()
  const ability = useAbility()
  const path = usePathHelper()
  const { organization } = useOrganizationContext()
  const canAccessFsnNetwork = canAccessFsnNetworkIntegration(ability)
  const canAccessCondolences = canAccessCondolencesIntegration(ability)
  const canAccessGoogleAnalytics = canAccessGoogleAnalyticsIntegration(ability)
  const canAccessGoogleTagManager = canAccessGoogleTagManagerIntegration(ability)
  const canAccessMetaPixel = canAccessMetaPixelIntegration(ability)
  const { data: fsnNetworkIntegration = {} } = useFsnNetworkIntegration({
    organizationId: orgId,
    enabled: canAccessFsnNetwork,
  })
  const { data: condolencesIntegration = {} } = useCondolencesIntegration({
    organizationId: orgId,
    enabled: canAccessCondolences,
  })
  const { data: googleAnalyticsIntegration = {} } = useGoogleAnalyticsIntegration({
    organizationId: orgId,
    enabled: canAccessGoogleAnalytics,
  })
  const { data: googleTagManagerIntegration = {} } = useGoogleTagManagerIntegration({
    organizationId: orgId,
    enabled: canAccessGoogleTagManager,
  })
  const { data: metaPixelIntegration = {} } = useMetaPixelIntegration({
    organizationId: orgId,
    enabled: canAccessMetaPixel,
  })
  const integrations = buildIntegrationSettingsUseCases({
    ability,
    condolencesIntegration,
    fsnNetworkIntegration,
    googleAnalyticsIntegration,
    googleTagManagerIntegration,
    metaPixelIntegration,
    organization,
    orgId,
    path,
  })

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {integrations.map((integration) => (
        <IntegrationSettingsCard key={integration.id} integration={integration} />
      ))}
    </div>
  )
}

export function IntegrationSettingsPage() {
  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={Network}
        title="Integrations"
        description="Connect outside services that send or receive order updates in our system."
      />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice>
            Integrations are organization-specific. Impersonate the florist organization you want to
            configure.
          </OrganizationContextNotice>
        }
      >
        <OrganizationIntegrationSettings />
      </IfGlobalOrg>
    </div>
  )
}
